import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Alert, ListGroup, Spinner } from 'react-bootstrap';
import CryptoJS from 'crypto-js';
import { getInheritanceList, addFileToInheritance } from '../../services/inheritance.service';
import useModalHistory from '../../hooks/useModalHistory';
const AddToInheritanceModal = ({ show, onHide, fileItem }) => {
  const [inheritances, setInheritances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedInheritance, setSelectedInheritance] = useState(null);

      useModalHistory(show, onHide, 'add-to-endowment-modal');

  
  // Load inheritances when modal opens
  useEffect(() => {
    if (show) {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setSelectedInheritance(null);
      
      loadInheritances();
    }
  }, [show]);
  
  // Load inheritance list
  const loadInheritances = async () => {
    try {
      const list = await getInheritanceList();
      setInheritances(list);
      
      // Auto-select if there's only one inheritance
      if (list.length === 1) {
        setSelectedInheritance(list[0]);
      }
    } catch (err) {
      setError(`Failed to load endowments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle item selection
  const handleSelectInheritance = (inheritance) => {
    setSelectedInheritance(inheritance);
  };
  
  // Handle add button click
  const handleAddToInheritance = async () => {
    if (!selectedInheritance || !fileItem) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      await addFileToInheritance(fileItem.path || fileItem.name, selectedInheritance.name);
      setSuccess(true);
      
      // Close after a delay on success
      setTimeout(() => {
        onHide();
      }, 1500);
    } catch (err) {
      setError(`Failed to add file to endowment: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Add to Endowment</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3">
            File successfully added to endowment!
          </Alert>
        )}
        
        {loading ? (
          <div className="text-center my-4">
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading endowments...</p>
          </div>
        ) : inheritances.length === 0 ? (
          <div className="text-center my-4">
            <i className="fas fa-folder-open fa-3x text-black mb-3"></i>
            <h5>No Endowments Found</h5>
            <p className="text-black">
              You need to set up at least one endowment in the Endowment Center before adding files.
            </p>
            <Button
              variant="primary"
              href="/endowment-centre"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-external-link-alt mr-2"></i>
              Go to Endowment Center
            </Button>
          </div>
        ) : (
          <>
            <p>
              Select an endowment to add <strong>{fileItem?.name}</strong> to:
            </p>
            
            <ListGroup className="inheritance-list mb-3">
              {inheritances.map((inheritance) => (
                <ListGroup.Item
                  key={inheritance.id}
                  active={selectedInheritance && selectedInheritance.id === inheritance.id}
                  onClick={() => handleSelectInheritance(inheritance)}
                  className="d-flex align-items-center"
                  action
                >
                  <div className="mr-3">
                    <i className="fas fa-user-shield fa-lg"></i>
                  </div>
                  <div>
                    <div className="font-weight-bold">
                      {inheritance.name.endsWith('.txt') 
                        ? inheritance.name.slice(0, -4) 
                        : inheritance.name}
                    </div>
                    <small className="text-black">
                      Last modified: {new Date(inheritance.lastModified).toLocaleDateString()}
                    </small>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
            
            {fileItem && selectedInheritance && (
              <Alert variant="info">
                <i className="fas fa-info-circle mr-2"></i>
                Adding <strong>{fileItem.name}</strong> to endowment <strong>
                  {selectedInheritance.name.endsWith('.txt') 
                    ? selectedInheritance.name.slice(0, -4) 
                    : selectedInheritance.name}
                </strong>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleAddToInheritance}
          disabled={!selectedInheritance || loading || submitting || success}
        >
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              Adding...
            </>
          ) : 'Add to Endowment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AddToInheritanceModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  fileItem: PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string,
    type: PropTypes.string
  })
};

export default AddToInheritanceModal;